/**
 * Where am I, and how do I get back to the workspace finder?
 *
 * The dashboard is only ever served from a tenant host (`{tenant}.{base}`) —
 * the tenant-router denies anything else. The sign-in screen uses this to close
 * the loop with `go.{base}`: a person types a subdomain on the finder, gets
 * 302'd here, and the login card echoes back the host they landed on so the
 * redirect reads as one continuous flow instead of a jump to a stranger.
 *
 * Derived from `window.location` only — no API call, nothing to fail, and it
 * works before the session is resolved.
 */

export interface WorkspaceHost {
  /** The tenant label, e.g. `demo`. Null when the host carries no subdomain. */
  workspace: string | null;
  /** The full host as shown to the user, e.g. `demo.qelm.app`. */
  host: string;
  /** Absolute URL of the workspace finder, or null when there isn't one. */
  finderUrl: string | null;
}

/** True for the local dev bases where there is no edge Worker in front. */
function isLocalBase(base: string): boolean {
  return base === 'localhost' || base === '127.0.0.1' || base === '0.0.0.0';
}

/**
 * Split a hostname into its tenant label and base domain.
 *
 * `demo.qelm.app`      → { workspace: 'demo', base: 'qelm.app' }
 * `demo.dev.qelm.app`  → { workspace: 'demo', base: 'dev.qelm.app' }
 * `demo.localhost`     → { workspace: 'demo', base: 'localhost' }
 * `qelm.app`           → { workspace: null }   (apex — not a tenant host)
 * `localhost`          → { workspace: null }
 */
export function resolveWorkspaceHost(
  hostname: string,
  protocol = 'https:',
  port = '',
): WorkspaceHost {
  const host = hostname.toLowerCase();
  const labels = host.split('.');

  if (labels.length < 2) {
    return { workspace: null, host, finderUrl: null };
  }

  const [first, ...rest] = labels;
  const base = rest.join('.');

  // A base with no dot of its own is an apex like `qelm.app`, NOT `{tenant}.{base}` —
  // `localhost` is the one exception, since `demo.localhost` is a real dev tenant host.
  if (!base.includes('.') && !isLocalBase(base)) {
    return { workspace: null, host, finderUrl: null };
  }

  const suffix = port ? `:${port}` : '';
  const finderUrl = isLocalBase(base) ? `${protocol}//${base}${suffix}` : `https://go.${base}`;

  return { workspace: first, host, finderUrl };
}

/** `resolveWorkspaceHost` bound to the live browser location. SSR-safe. */
export function currentWorkspaceHost(): WorkspaceHost {
  if (typeof window === 'undefined') {
    return { workspace: null, host: '', finderUrl: null };
  }
  const { hostname, protocol, port } = window.location;
  return resolveWorkspaceHost(hostname, protocol, port);
}
